import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

import { PageHelp } from './page-help';

interface PageHeaderProps {
  /** Small uppercase label above the title — the section or object type. */
  eyebrow?: string;
  title: ReactNode;
  /** One sentence: what this page is for. */
  description?: ReactNode;
  /** Inline next to the title, e.g. a status badge. */
  badge?: ReactNode;
  /** Right-aligned buttons. */
  actions?: ReactNode;
  /** A line of facts under the description (who / when / ids). */
  meta?: ReactNode;
  /** Back link shown above the title on detail pages. */
  back?: { to: string; label: string; search?: Record<string, string> };
  /** "How this works" — a (?) next to the title opening a short explanation (use instead of a top banner). */
  help?: ReactNode;
}

/**
 * The single page header used by every Content page — same type scale, spacing
 * and action placement, so users always know where the title, explanation and
 * primary action are.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  badge,
  actions,
  meta,
  back,
  help,
}: PageHeaderProps) {
  return (
    <header className="flex flex-col gap-3">
      {back && (
        <Link
          to={back.to}
          {...(back.search && { search: back.search })}
          className="text-muted-foreground hover:text-foreground flex w-fit cursor-pointer items-center gap-1.5 text-sm transition-colors"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          {eyebrow && (
            <p className="text-primary text-xs font-semibold tracking-wider uppercase">{eyebrow}</p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-foreground text-[22px] leading-tight font-semibold tracking-tight">
              {title}
            </h1>
            {help && <PageHelp label="How this page works">{help}</PageHelp>}
            {badge}
          </div>
          {description && (
            <p className="text-muted-foreground max-w-3xl text-[15px] leading-relaxed">
              {description}
            </p>
          )}
          {meta && (
            <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              {meta}
            </div>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
